import { motion } from "framer-motion";
import { RefreshCw, Undo2, Blocks, Sparkles } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

export default function OurServices() {
  return (
    <section id="services" data-testid="services-section" className="relative py-24 md:py-32 bg-pinkSoft">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <motion.span {...fadeUp} transition={{ duration: 0.6 }} className="kicker">
          What We Offer
        </motion.span>

        <motion.h2
          {...fadeUp}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 font-editorial text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl"
        >
          Not just delivery. <span className="italic text-pinkDeep">A better way to shop.</span>
        </motion.h2>

        <motion.p
          {...fadeUp}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          className="mt-8 max-w-2xl text-base md:text-lg text-foreground/70 leading-relaxed"
        >
          Mumzo is built around the everyday reality of parenting, from the
          essentials you can't do without, to the little things that make
          your little one smile.
        </motion.p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-6 md:grid-rows-2 gap-4 md:gap-5">
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
            data-testid="service-card-subscribe"
            className="md:col-span-4 md:row-span-2 relative overflow-hidden rounded-3xl bg-pinkDeep text-white p-8 md:p-10 flex flex-col justify-between min-h-[280px]"
          >
            <div
              aria-hidden
              className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10 blur-2xl"
            />
            <div className="relative w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-white" aria-hidden />
            </div>
            <div className="relative mt-8">
              <div className="font-editorial text-4xl md:text-5xl leading-tight">
                Subscribe &amp; forget.
              </div>
              <p className="mt-4 text-sm md:text-base text-white/80 max-w-sm leading-relaxed">
                Diapers, wipes, formula, on a schedule that matches your
                baby's pace. Auto-delivered before you ever run out.
              </p>
            </div>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.22 }}
            data-testid="service-card-try-buy"
            className="md:col-span-2 rounded-3xl bg-white border border-border/70 p-7 flex flex-col justify-between"
          >
            <div className="w-11 h-11 rounded-2xl bg-blush flex items-center justify-center">
              <Undo2 className="w-5 h-5 text-pinkDeep" aria-hidden />
            </div>
            <div className="mt-6">
              <div className="font-editorial text-2xl md:text-3xl leading-tight">
                Try &amp; buy.
              </div>
              <p className="mt-2 text-sm text-foreground/65 leading-relaxed">
                Order it, try it on baby, keep what works. Pay only for what
                stays.
              </p>
            </div>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.29 }}
            data-testid="service-card-toys"
            className="md:col-span-2 rounded-3xl bg-pinkSoft border border-rose/40 p-7 flex flex-col justify-between"
          >
            <div className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center">
              <Blocks className="w-5 h-5 text-pinkDeep" aria-hidden />
            </div>
            <div className="mt-6">
              <div className="font-editorial text-2xl md:text-3xl leading-tight">
                The deepest toy shelf.
              </div>
              <p className="mt-2 text-sm text-foreground/65 leading-relaxed">
                Hundreds of toys across every age and stage, not just the
                five bestsellers every app carries.
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div
          {...fadeUp}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.36 }}
          data-testid="service-card-recommend"
          className="mt-4 md:mt-5 rounded-3xl bg-white border border-border/70 p-7 flex items-start gap-5"
        >
          <div className="shrink-0 w-11 h-11 rounded-2xl bg-blush flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-pinkDeep" aria-hidden />
          </div>
          <div>
            <div className="font-editorial text-2xl md:text-3xl leading-tight">
              Recommendations that grow with your baby.
            </div>
            <p className="mt-2 text-sm md:text-base text-foreground/65 leading-relaxed">
              Daily-curated picks based on your baby's exact age, so you're
              never guessing what's next.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
